import React, { useState } from 'react';
import { NavLink, Link, Outlet, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Linkedin, ShieldCheck, BadgeCheck, Lock } from 'lucide-react';
import freshroadLogo from '../../../images/freshroadlogo.png';
import { T, SURFACE, HAIRLINE, SHADOW, Band, PrimaryButton, DemoLink } from './siteUI';
import { SOLUTIONS } from './solutionsData';
import '../demo.css';

/* =========================================================
   Public marketing site shell — same design language as the
   dashboard demo (teal primary, Poppins, slate neutrals).
   Design tokens live in ./siteUI.js; use them rather than
   inlining utility strings, so the four pages stay in step.
   ========================================================= */

/* Footer/nav labels that map to a real route. PRODUCT_LINKS also feeds the
   Products dropdown, so Pricing is kept separate — it is not a product. */
export const PRODUCT_LINKS = [
  { label: 'Conversational AI', to: '/site/conversational-ai' },
  { label: 'Teammate', to: '/site/teammate' },
  { label: 'Orbiter', to: '/site/orbiter' },
];

/* Nav entries beyond the two dropdowns. Every entry needs a real route —
   Resources was a dropdown with nothing behind it and is gone until there is
   something to put in it. */
const NAV_MENUS = [{ label: 'Company', to: '/site/company' }];

/* Solution links come from solutionsData so the nav, the index page and
   the detail pages can never disagree about what exists. */
export const SOLUTION_LINKS = SOLUTIONS.map(({ slug, name }) => ({
  label: name,
  to: `/site/solutions/${slug}`,
}));

/* Each product signs in to its own area. Conversational AI lives in this app;
   Teammate and Orbiter are their own products on their own domains. */
export const PRODUCT_LOGIN = {
  conversational: '/dashboard/customer-ai/agents',
  teammate: 'https://teammate.freshroad.ai',
  orbiter: 'https://orbiter.freshroad.com',
};

const isExternal = (u) => typeof u === 'string' && /^https?:\/\//.test(u);
const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600';
// "Log in" is a real button (white, teal text) so every product page has a visible login CTA
// next to "Book a demo"; "View pricing" stays a lighter text link.
const PRODUCT_BTN_CLASS = `inline-flex items-center justify-center whitespace-nowrap rounded-xl bg-white px-6 py-3 text-[14px] font-semibold text-teal-700 transition-colors hover:bg-teal-50 ${HAIRLINE} ${SHADOW.card} ${FOCUS_RING}`;
const PRODUCT_TEXT_CLASS = `rounded-lg text-[13.5px] font-semibold text-teal-700 transition-colors hover:text-teal-800 ${FOCUS_RING}`;

/* An internal route uses <Link>; an absolute product URL (teammate.freshroad.ai,
   orbiter.freshroad.com) must be a plain anchor — <Link to> can't leave the app. */
const ProductLink = ({ href, className, children }) =>
  isExternal(href) ? (
    <a href={href} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  ) : (
    <Link to={href} className={className}>
      {children}
    </Link>
  );

/** Per-product "Log in" button (and optional "View pricing"), for a product hero. */
export const ProductLinks = ({ login, pricing }) => (
  <span className="flex items-center gap-4">
    <ProductLink href={login} className={PRODUCT_BTN_CLASS}>Log in</ProductLink>
    {pricing && <ProductLink href={pricing} className={PRODUCT_TEXT_CLASS}>View pricing</ProductLink>}
  </span>
);

/* Social accounts. Only entries with a real href are rendered, so a dead icon
   can never reach the footer; adding an account means adding a line here. */
const SOCIAL_LINKS = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/freshroad', Icon: Linkedin },
];

/* Audited controls, shown as a strip along the bottom of the footer. */
const COMPLIANCE = [
  { name: 'HIPAA', sub: 'Compliant', icon: ShieldCheck },
  { name: 'ISO 27001', sub: 'Certified', icon: Lock },
  { name: 'SOC 2', sub: 'Type II compliant', icon: BadgeCheck },
];

/* Every entry carries its own route, so a link can only exist once it leads
   somewhere. Docs, Blog, Case Studies, Help Center, For Startups, For
   Enterprises, Careers, Partners and Contact Us were labels with no page and
   are gone until there is content behind them. */
const FOOTER_COLS = [
  { title: 'Products', links: [...PRODUCT_LINKS, { label: 'Pricing', to: '/site/pricing' }] },
  {
    title: 'Solutions',
    links: [
      { label: 'By Use Case', to: '/site/solutions' },
      { label: 'By Industry', to: '/site/solutions' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us', to: '/site/company' },
      { label: 'Privacy Policy', to: '/site/privacy' },
      { label: 'Terms of Service', to: '/site/terms' },
    ],
  },
];

/* ---------- shared page pieces ---------- */

export const Breadcrumb = ({ trail }) => (
  <div className="flex items-center gap-1.5 text-[12.5px] text-slate-400">
    {trail.map((t, i) => (
      <React.Fragment key={t}>
        {i > 0 && <ChevronRight size={12} />}
        <span className={i === trail.length - 1 ? 'text-slate-600' : ''}>{t}</span>
      </React.Fragment>
    ))}
  </div>
);

/* Kept for the three product pages that already import it. `tinted` is now a
   no-op: it used to gate the white background, so every call site had to pass
   it to get the normal look. New code should use <Band> from ./siteUI. */
export const SectionBand = ({ title, subtitle, children }) => (
  <Band title={title} subtitle={subtitle}>
    {children}
  </Band>
);

export const CtaBand = ({ title, subtitle, primary, secondary }) => (
  <section
    className={`flex flex-col items-center justify-between gap-6 ${SURFACE.band} px-8 py-10 md:flex-row md:px-12`}
  >
    <div className="flex items-center gap-5">
      <span className="hidden h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 md:flex">
        <img src={freshroadLogo} alt="" className="h-7 w-7 object-contain" />
      </span>
      <div>
        <p className={T.h3}>{title}</p>
        <p className={`mt-1.5 ${T.body}`}>{subtitle}</p>
      </div>
    </div>
    <div className="flex shrink-0 items-center gap-3">
      {/^book a demo$/i.test(String(primary)) ? (
        <DemoLink>{primary}</DemoLink>
      ) : (
        <PrimaryButton>{primary}</PrimaryButton>
      )}
      {/* Only rendered when a caller passes one — a second button with no
          destination is worse than no second button. */}
      {secondary && (
        <Link
          to={secondary.to}
          className={`rounded-xl bg-white px-6 py-3 text-[14px] font-semibold text-teal-700 transition-colors hover:bg-teal-50 ${HAIRLINE} ${SHADOW.card} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600`}
        >
          {secondary.label}
        </Link>
      )}
    </div>
  </section>
);

/* ---------- layout ---------- */

const DemoSiteLayout = () => {
  const [productsOpen, setProductsOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="demo-ui min-h-screen bg-[#F7F9FB] text-slate-900">
      {/* Top nav */}
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-[68px] max-w-[1280px] items-center gap-10 px-6">
          <Link to="/site" className="flex items-center">
            <img src={freshroadLogo} alt="FreshRoad" className="h-8 object-contain" />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {/* Products dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setProductsOpen((o) => !o);
                  setSolutionsOpen(false);
                }}
                onBlur={() => setTimeout(() => setProductsOpen(false), 150)}
                /* Active only on an actual product route — startsWith('/site')
                   also lit this up on the landing and pricing pages. */
                className={`flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[13.5px] font-medium transition-colors ${
                  PRODUCT_LINKS.some((p) => location.pathname === p.to)
                    ? 'text-slate-900'
                    : 'text-slate-600'
                } hover:bg-slate-50`}
              >
                Products
                <ChevronDown size={14} className={`text-slate-400 transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
              </button>
              {productsOpen && (
                <div
                  className={`absolute left-0 top-full mt-1.5 w-[230px] rounded-2xl bg-white p-1.5 ${HAIRLINE} ${SHADOW.raised}`}
                >
                  {PRODUCT_LINKS.map(({ label, to }) => (
                    <NavLink
                      key={to}
                      to={to}
                      className={({ isActive }) =>
                        `block rounded-xl px-3.5 py-2.5 text-[13.5px] font-medium transition-colors ${
                          isActive ? 'bg-teal-50 text-teal-700' : 'text-slate-600 hover:bg-slate-50'
                        }`
                      }
                    >
                      {label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            {/* Solutions dropdown — same shape as Products, listing every
                solution plus a link to the index. */}
            <div className="relative">
              <button
                onClick={() => {
                  setSolutionsOpen((o) => !o);
                  setProductsOpen(false);
                }}
                onBlur={() => setTimeout(() => setSolutionsOpen(false), 150)}
                className={`flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[13.5px] font-medium transition-colors ${
                  location.pathname.startsWith('/site/solutions') ? 'text-slate-900' : 'text-slate-600'
                } hover:bg-slate-50`}
              >
                Solutions
                <ChevronDown
                  size={14}
                  className={`text-slate-400 transition-transform ${solutionsOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {solutionsOpen && (
                <div
                  className={`absolute left-0 top-full mt-1.5 w-[250px] rounded-2xl bg-white p-1.5 ${HAIRLINE} ${SHADOW.raised}`}
                >
                  {SOLUTION_LINKS.map(({ label, to }) => (
                    <NavLink
                      key={to}
                      to={to}
                      className={({ isActive }) =>
                        `block rounded-xl px-3.5 py-2.5 text-[13.5px] font-medium transition-colors ${
                          isActive ? 'bg-teal-50 text-teal-700' : 'text-slate-600 hover:bg-slate-50'
                        }`
                      }
                    >
                      {label}
                    </NavLink>
                  ))}
                  <NavLink
                    to="/site/solutions"
                    end
                    className={({ isActive }) =>
                      `mt-1 block rounded-xl border-t border-slate-100 px-3.5 pb-2 pt-3 text-[12.5px] font-semibold transition-colors ${
                        isActive ? 'text-teal-700' : 'text-slate-500 hover:text-teal-700'
                      }`
                    }
                  >
                    All solutions
                  </NavLink>
                </div>
              )}
            </div>

            {NAV_MENUS.map(({ label, to }) => (
              <NavLink
                key={label}
                to={to}
                className={({ isActive }) =>
                  `rounded-lg px-3.5 py-2 text-[13.5px] font-medium hover:bg-slate-50 ${
                    isActive ? 'text-teal-700' : 'text-slate-600'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      {/* Page */}
      <main className="mx-auto max-w-[1280px] space-y-4 px-6 pb-6 pt-5">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200/70 bg-white">
        <div className="mx-auto grid max-w-[1280px] grid-cols-2 gap-10 px-6 py-12 md:grid-cols-4">
          <div className="col-span-2 md:col-span-1">
            <img src={freshroadLogo} alt="FreshRoad" className="h-7 object-contain" />
            <p className="mt-3 max-w-[200px] text-[12.5px] leading-relaxed text-slate-500">
              Business AI for every business.
            </p>
          </div>
          {FOOTER_COLS.map(({ title, links }) => (
            <div key={title}>
              <p className="text-[13px] font-semibold">{title}</p>
              <ul className="mt-3 space-y-2">
                {links.map(({ label, to }) => (
                  <li key={label}>
                    <Link to={to} className="text-[12.5px] text-slate-500 hover:text-teal-700">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {/* Compliance strip */}
        <div className="mx-auto max-w-[1280px] px-6">
          <div className="flex flex-wrap items-center gap-x-10 gap-y-4 border-t border-slate-100 py-6">
            {COMPLIANCE.map(({ name, sub, icon: Icon }) => (
              <div key={name} className="flex items-center gap-2.5">
                <Icon size={20} strokeWidth={1.6} className="shrink-0 text-teal-600" />
                <span className="leading-tight">
                  <span className="block text-[12.5px] font-bold">{name}</span>
                  <span className="block text-[11px] text-slate-500">{sub}</span>
                </span>
              </div>
            ))}
            <div className="ml-auto flex items-center gap-3">
              <span className="text-[12px] font-semibold text-slate-500">Follow us</span>
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${label} (opens in a new tab)`}
                  className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-50 hover:text-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                >
                  <Icon size={17} strokeWidth={1.8} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-100">
          <div className="mx-auto max-w-[1280px] px-6 py-4 text-[12px] text-slate-400">
            <span>© 2026 FreshRoad Inc. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DemoSiteLayout;
